import React, { useState, useEffect } from 'react';
import { updateVisitNote, fetchVisitNoteById } from '../../services/api';

const VisitNotesPanel = ({ isOpen, noteId, noteContent, onClose, onSave, authData }) => {
  const [editedContent, setEditedContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [structuredSections, setStructuredSections] = useState(null);
  const [isStructuredMode, setIsStructuredMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch note content from API when panel opens
  useEffect(() => {
    if (isOpen && noteId && authData?.api_token) {
      console.log('[VisitNotesPanel] Fetching note content for ID:', noteId);
      setLoading(true);
      setError(null);
      
      fetchVisitNoteById(authData.api_token, noteId)
        .then(response => {
          console.log('[VisitNotesPanel] Fetched note data:', response);
          const content = response.content || '';
          
          // Try to detect if the content is structured JSON
          try {
            const parsed = JSON.parse(content);
            if (parsed && typeof parsed === 'object' && 
                (parsed.chiefComplaint !== undefined || parsed.historyOfPresentIllness !== undefined)) {
              setStructuredSections(parsed);
              setIsStructuredMode(true);
              setEditedContent(''); // Clear text content for structured mode
            } else {
              setEditedContent(content);
              setStructuredSections(null);
              setIsStructuredMode(false);
            }
          } catch (e) {
            // Not JSON, treat as regular text
            setEditedContent(content);
            setStructuredSections(null);
            setIsStructuredMode(false);
          }
        })
        .catch(err => {
          console.error('[VisitNotesPanel] Failed to fetch note:', err);
          setError('Failed to load note content: ' + err.message);
          // Initialize empty for new notes
          setStructuredSections({
            chiefComplaint: '',
            historyOfPresentIllness: '',
            reviewOfSystems: '',
            physicalExamination: '',
            assessment: '',
            plan: ''
          });
          setIsStructuredMode(true);
          setEditedContent('');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (isOpen && !noteId) {
      // No note selected - show empty state
      setStructuredSections(null);
      setIsStructuredMode(false);
      setEditedContent('');
      setError(null);
      setSaveMessage(null);
    }
  }, [isOpen, noteId, authData?.api_token]);

  // Handle structured section updates
  const handleSectionChange = (sectionKey, value) => {
    setStructuredSections(prev => ({
      ...prev,
      [sectionKey]: value
    }));
  };

  // Convert structured sections to formatted text
  const convertStructuredToText = (sections) => {
    let formatted = "";
    if (sections.chiefComplaint) {
      formatted += `Chief Complaint:\n${sections.chiefComplaint}\n\n`;
    }
    if (sections.historyOfPresentIllness) {
      formatted += `History of Present Illness:\n${sections.historyOfPresentIllness}\n\n`;
    }
    if (sections.reviewOfSystems) {
      formatted += `Review of Systems:\n${sections.reviewOfSystems}\n\n`;
    }
    if (sections.physicalExamination) {
      formatted += `Physical Examination:\n${sections.physicalExamination}\n\n`;
    }
    if (sections.assessment) {
      formatted += `Assessment:\n${sections.assessment}\n\n`;
    }
    if (sections.plan) {
      formatted += `Plan:\n${sections.plan}\n\n`;
    }
    return formatted.trim();
  };

  const handleCopyNote = async () => {
    const contentToCopy = isStructuredMode 
      ? convertStructuredToText(structuredSections)
      : editedContent;
      
    if (contentToCopy) {
      try {
        await navigator.clipboard.writeText(contentToCopy);
        setSaveMessage('Note copied to clipboard!');
        setTimeout(() => setSaveMessage(null), 2000);
      } catch (err) {
        setError('Failed to copy to clipboard');
      }
    }
  };

  const handleDownloadNote = () => {
    const contentToDownload = isStructuredMode 
      ? convertStructuredToText(structuredSections)
      : editedContent;
      
    if (contentToDownload) {
      const blob = new Blob([contentToDownload], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visit-note-${noteId || 'edited'}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCancel = () => {
    if (isStructuredMode) {
      // Reset structured sections to original
      try {
        const parsed = JSON.parse(noteContent);
        setStructuredSections(parsed);
      } catch (e) {
        // Fallback
        setStructuredSections({
          chiefComplaint: '',
          historyOfPresentIllness: '',
          reviewOfSystems: '',
          physicalExamination: '',
          assessment: '',
          plan: ''
        });
      }
    } else {
      setEditedContent(noteContent); // Reset to original
    }
    setError(null);
    setSaveMessage(null);
    onClose();
  };

  const parseNoteIntoSections = (noteText) => {
    if (!noteText) return {};
    
    const sections = {};
    
    // Common medical note section patterns
    const sectionPatterns = [
      { key: 'chiefComplaint', patterns: ['Chief Complaint', 'CC', 'Complaint'] },
      { key: 'historyOfPresentIllness', patterns: ['History of Present Illness', 'HPI', 'Present Illness'] },
      { key: 'reviewOfSystems', patterns: ['Review of Systems', 'ROS', 'Systems Review'] },
      { key: 'pastMedicalHistory', patterns: ['Past Medical History', 'PMH', 'Medical History'] },
      { key: 'physicalExamination', patterns: ['Physical Examination', 'Physical Exam', 'PE', 'Examination'] },
      { key: 'assessment', patterns: ['Assessment', 'Impression', 'Diagnosis'] },
      { key: 'plan', patterns: ['Plan', 'Treatment Plan'] },
      { key: 'assessmentAndPlan', patterns: ['Assessment and Plan', 'Assessment & Plan', 'A&P'] }
    ];
    
    // Split the note by lines and process
    const lines = noteText.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;
      
      // Check if this line starts a new section
      let foundSection = null;
      for (const section of sectionPatterns) {
        for (const pattern of section.patterns) {
          if (trimmedLine.toLowerCase().includes(pattern.toLowerCase() + ':') || 
              trimmedLine.toLowerCase().startsWith(pattern.toLowerCase())) {
            foundSection = section.key;
            break;
          }
        }
        if (foundSection) break;
      }
      
      if (foundSection) {
        // Save previous section if it exists
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        
        // Start new section
        currentSection = foundSection;
        currentContent = [trimmedLine];
      } else if (currentSection) {
        // Add to current section
        currentContent.push(trimmedLine);
      } else {
        // No section yet, might be general content
        if (!sections.general) sections.general = '';
        sections.general += trimmedLine + '\n';
      }
    }
    
    // Save the last section
    if (currentSection && currentContent.length > 0) {
      sections[currentSection] = currentContent.join('\n').trim();
    }
    
    return sections;
  };

  const handleSendToCoreServer = () => {
    let sectionsToSend;
    let fullContent;
    
    if (isStructuredMode) {
      // Use structured sections directly
      sectionsToSend = {
        chiefComplaint: structuredSections.chiefComplaint || '',
        historyOfPresentIllness: structuredSections.historyOfPresentIllness || '',
        reviewOfSystems: structuredSections.reviewOfSystems || '',
        physicalExamination: structuredSections.physicalExamination || '',
        assessment: structuredSections.assessment || '',
        plan: structuredSections.plan || ''
      };
      fullContent = convertStructuredToText(structuredSections);
    } else {
      if (!editedContent || !editedContent.trim()) {
        setError('No note content to send');
        return;
      }
      // Parse the note into sections for unstructured content
      sectionsToSend = parseNoteIntoSections(editedContent);
      fullContent = editedContent.trim();
    }
    
    try {
      // Prepare the message payload
      const payload = {
        type: 'VISIT_NOTE_SECTIONS_UPDATE',
        data: {
          noteId: noteId,
          sections: sectionsToSend,
          fullNoteContent: fullContent,
          timestamp: new Date().toISOString(),
          wordCount: fullContent.split(/\s+/).length,
          structured: isStructuredMode
        }
      };
      
      // Send to Core Server via postMessage
      if (window.parent !== window) {
        window.parent.postMessage(payload, '*');
        setSaveMessage('Note sections sent to Core Server successfully!');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        // For testing - log to console if not in iframe
        console.log('PostMessage payload (would send to Core Server):', payload);
        setSaveMessage('Note sections prepared (check console for testing)');
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      setError('Failed to send note to Core Server: ' + err.message);
    }
  };
  const handleSaveNote = async () => {
    let contentToSave;
    
    if (isStructuredMode) {
      // Convert structured sections to formatted text for saving
      contentToSave = convertStructuredToText(structuredSections);
      if (!contentToSave.trim()) {
        setError('Note content cannot be empty');
        return;
      }
    } else {
      if (!editedContent || !editedContent.trim()) {
        setError('Note content cannot be empty');
        return;
      }
      contentToSave = editedContent.trim();
    }
    
    setSaving(true);
    setError(null);

    try {
      if (noteId) {
        // Update existing note via API
        const response = await updateVisitNote(
          authData.api_token,
          noteId,
          contentToSave
        );
        if (response.success) {
          setSaveMessage('Note saved successfully!');
          setTimeout(() => setSaveMessage(null), 3000);
          // Notify parent component
          if (onSave) {
            onSave(contentToSave);
          }
        }
      } else {
        // New note - just notify parent
        if (onSave) {
          onSave(contentToSave);
        }
        setSaveMessage('Note updated!');
        setTimeout(() => setSaveMessage(null), 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`template-editor-panel ${isOpen ? 'open' : ''}`}>
      <div className="scribe-panel-header">
        <h4 className="popup-title">Edit Note</h4>
        <button className="close-button" onClick={onClose}>×</button>
      </div>

      <div className="scribe-panel-content" style={{ padding: '16px', overflow: 'auto' }}>
        {error && <div className="ankr-auth-status error">{error}</div>}
        
        {saveMessage && (
          <div className="alert alert-success small mb-3">
            {saveMessage}
          </div>
        )}

        {loading && (
          <div className="text-center mb-3">
            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
            Loading note content...
          </div>
        )}

        {/* Empty state when no note is selected */}
        {!loading && !noteId && !error && (
          <div className="empty-state text-center py-5">
            <div className="empty-icon">📝</div>
            <p>No notes found</p>
            <small>Select a case to view notes, or create one</small>
          </div>
        )}

        {/* Mode Toggle */}
        {(noteId || isStructuredMode) && isStructuredMode && (
          <div className="mb-3">
            <button 
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setIsStructuredMode(false);
                setEditedContent(convertStructuredToText(structuredSections));
              }}
            >
              📝 Switch to Text Mode
            </button>
            <small className="text-muted ms-2">Currently editing structured sections</small>
          </div>
        )}

        {/* Only show editors when there's a noteId or we're in structured mode */}
        {(noteId || isStructuredMode) && (
          isStructuredMode ? (
            /* Structured Section Editor */
          <div className="structured-editor">
            <label className="mb-3"><strong>Medical Note Sections</strong></label>
            
            {/* Chief Complaint */}
            <div className="ankr-form-group mb-3">
              <label><strong>Chief Complaint</strong></label>
              <textarea
                className="form-control"
                value={structuredSections?.chiefComplaint || ''}
                onChange={(e) => handleSectionChange('chiefComplaint', e.target.value)}
                rows="2"
                style={{ fontSize: '14px', resize: 'vertical' }}
                disabled={saving}
                placeholder="Brief description of the chief complaint..."
              />
            </div>

            {/* History of Present Illness */}
            <div className="ankr-form-group mb-3">
              <label><strong>History of Present Illness</strong></label>
              <textarea
                className="form-control"
                value={structuredSections?.historyOfPresentIllness || ''}
                onChange={(e) => handleSectionChange('historyOfPresentIllness', e.target.value)}
                rows="4"
                style={{ fontSize: '14px', resize: 'vertical' }}
                disabled={saving}
                placeholder="Detailed history of the present illness..."
              />
            </div>

            {/* Review of Systems */}
            <div className="ankr-form-group mb-3">
              <label><strong>Review of Systems</strong></label>
              <textarea
                className="form-control"
                value={structuredSections?.reviewOfSystems || ''}
                onChange={(e) => handleSectionChange('reviewOfSystems', e.target.value)}
                rows="3"
                style={{ fontSize: '14px', resize: 'vertical' }}
                disabled={saving}
                placeholder="Review of systems findings..."
              />
            </div>

            {/* Physical Examination */}
            <div className="ankr-form-group mb-3">
              <label><strong>Physical Examination</strong></label>
              <textarea
                className="form-control"
                value={structuredSections?.physicalExamination || ''}
                onChange={(e) => handleSectionChange('physicalExamination', e.target.value)}
                rows="4"
                style={{ fontSize: '14px', resize: 'vertical' }}
                disabled={saving}
                placeholder="Physical examination findings..."
              />
            </div>

            {/* Assessment */}
            <div className="ankr-form-group mb-3">
              <label><strong>Assessment</strong></label>
              <textarea
                className="form-control"
                value={structuredSections?.assessment || ''}
                onChange={(e) => handleSectionChange('assessment', e.target.value)}
                rows="3"
                style={{ fontSize: '14px', resize: 'vertical' }}
                disabled={saving}
                placeholder="Clinical assessment and diagnosis..."
              />
            </div>

            {/* Plan */}
            <div className="ankr-form-group mb-3">
              <label><strong>Plan</strong></label>
              <textarea
                className="form-control"
                value={structuredSections?.plan || ''}
                onChange={(e) => handleSectionChange('plan', e.target.value)}
                rows="4"
                style={{ fontSize: '14px', resize: 'vertical' }}
                disabled={saving}
                placeholder="Treatment plan and follow-up instructions..."
              />
            </div>
          </div>
        ) : (
          /* Text Editor Mode */
          <div className="ankr-form-group">
            <label>Note Content</label>
            <textarea
              className="form-control"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              rows="15"
              style={{ 
                fontFamily: 'monospace',
                fontSize: '14px',
                resize: 'vertical',
                minHeight: '400px'
              }}
              disabled={saving}
              placeholder="Enter your visit note content here..."
              autoFocus
            />
            <div className="mt-2">
              <small className="text-muted">
                Tip: Use standard medical note format - Chief Complaint, History, Examination, Assessment, Plan
              </small>
              {editedContent && (
                <div className="mt-2">
                  <small className="text-info">
                    <strong>Detected Sections:</strong> {
                      Object.keys(parseNoteIntoSections(editedContent)).length > 0 
                        ? Object.keys(parseNoteIntoSections(editedContent)).join(', ')
                        : 'None detected (will send as general content)'
                    }
                  </small>
                </div>
              )}
            </div>
          </div>
          )
        )}
      </div>

      {/* Status bar with actions */}
      <div className="scribe-status-bar">
        <div className="">
          <button 
            className="btn btn-success btn-sm me-2" 
            onClick={handleSaveNote}
            disabled={saving || (isStructuredMode ? !convertStructuredToText(structuredSections).trim() : !editedContent || !editedContent.trim())}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
        <div className="status-right">
          <small className="text-muted">
            {isStructuredMode 
              ? (convertStructuredToText(structuredSections).trim() ? `${convertStructuredToText(structuredSections).trim().split(/\s+/).length} words` : '0 words')
              : (editedContent ? `${editedContent.trim().split(/\s+/).length} words` : '0 words')
            }
          </small>
        </div>
      </div>
    </div>
  );
};

export default VisitNotesPanel;